import React, { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';

export type OpsHoverTipPlacement = 'above' | 'below' | 'right';

export type OpsHoverTipState = {
  x: number;
  y: number;
  title?: string;
  text: string;
  /** Default `above` (cursor tip). `below` anchors under an element, `right` beside it. */
  placement?: OpsHoverTipPlacement;
};

/** Portaled hover tip — same surface as scorecard / settings tooltips. */
export function OpsHoverTipPortal({ tip }: { tip: OpsHoverTipState | null }) {
  if (!tip || typeof document === 'undefined') return null;
  const placement = tip.placement ?? 'above';
  // Centred tips are kept off the viewport edges; a `right` tip starts at its anchor.
  const tipX =
    placement === 'right'
      ? tip.x
      : Math.min(Math.max(tip.x, 160), window.innerWidth - 160);
  return createPortal(
    <div
      className={`sc-help-tip sc-help-tip-portal${
        tip.title ? ' ops-hover-tip-rich' : ''
      }${placement === 'below' ? ' sc-help-tip-below' : ''}${
        placement === 'right' ? ' sc-help-tip-right' : ''
      }`}
      style={{ left: tipX, top: tip.y }}
      role="tooltip"
    >
      {tip.title ? (
        <>
          <strong className="ops-hover-tip-title">{tip.title}</strong>
          <span className="ops-hover-tip-body">{tip.text}</span>
        </>
      ) : (
        tip.text
      )}
    </div>,
    document.body,
  );
}

export function useOpsHoverTip() {
  const [tip, setTip] = useState<OpsHoverTipState | null>(null);

  const show = useCallback(
    (e: React.MouseEvent, payload: { title?: string; text: string }) => {
      setTip({
        x: e.clientX,
        y: e.clientY - 8,
        title: payload.title,
        text: payload.text,
        placement: 'above',
      });
    },
    [],
  );

  /** Fixed tip anchored to an element (does not follow the cursor). */
  const showAnchored = useCallback(
    (
      el: HTMLElement,
      payload: { title?: string; text: string },
      placement: OpsHoverTipPlacement = 'below',
    ) => {
      const r = el.getBoundingClientRect();
      setTip({
        x: placement === 'right' ? r.right : r.left + r.width / 2,
        y:
          placement === 'right'
            ? r.top + r.height / 2
            : placement === 'below'
              ? r.bottom
              : r.top,
        title: payload.title,
        text: payload.text,
        placement,
      });
    },
    [],
  );

  const move = useCallback((e: React.MouseEvent) => {
    setTip(prev =>
      prev && (prev.placement ?? 'above') === 'above'
        ? { ...prev, x: e.clientX, y: e.clientY - 8 }
        : prev,
    );
  }, []);

  const hide = useCallback(() => setTip(null), []);

  return { tip, show, showAnchored, move, hide };
}
